function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    newNode.querySelectorAll("legend").forEach((newLegend, index) => {
      let spanContent = newLegend.querySelector("span").innerHTML;
      let stickyLegend = document.querySelectorAll(".sticky-options legend")[
        index
      ];

      if (stickyLegend) {
        stickyLegend.querySelector("span").textContent = spanContent;
      }
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    if (document.querySelector("product-slider")) {
      document.querySelector("product-slider").initProductSlider();
    }

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
      if (this.input.value == "0") {
        this.closest("quantity-popover")
          .querySelector("cart-remove-button")
          .click();
      }
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    if (document.querySelectorAll("body.lock-overflow")) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class UpsellProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeUpsellProduct(this.dataset.productId);
  }

  initializeUpsellProduct(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const upsellProduct = html.querySelector("upsell-product");

        if (
          upsellProduct &&
          upsellProduct.querySelectorAll(".cart__upsell").length > 0
        ) {
          this.innerHTML = upsellProduct.innerHTML;

          new Swiper("#CartDrawer .upsell-product-swiper", {
            slidesPerView: 1.1,
            spaceBetween: 12,
            loop: false,
            pagination: {
              el: "#CartDrawer .swiper-pagination",
              clickable: true,
            },
          });
        } else if (
          upsellProduct &&
          upsellProduct.closest(".cart-upsell-section")
        ) {
          upsellProduct.closest(".cart-upsell-section").classList.add("hidden");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}
customElements.define("upsell-product", UpsellProduct);

class AddItem extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  handleClick(event) {
    this.classList.add("has-loading");

    let newId = this.dataset.newId;
    let oldId = this.dataset.oldId;

    let updates = {
      [newId]: 1,
      [oldId]: 0,
    };

    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((product) => {
        const drawerSectionId =
          document.querySelector("cart-drawer").dataset.id;
        fetch(`${routes.cart_url}?section_id=${drawerSectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            document.querySelector("cart-drawer .drawer__inner").innerHTML =
              html.querySelector("cart-drawer .drawer__inner").innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      })
      .catch((error) => console.error("Error:", error));
  }
}
customElements.define("add-item", AddItem);

class QuickShop extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  handleClick(event) {
    let dataHandle = this.dataset.handle;
    let $this = this;
    $this.classList.add("has-loading");

    var holder = document.getElementById("TemplateQuickShopHolder");
    var url = "/products/" + dataHandle + "?view=quick-view";

    url = url.replace("//", "/");

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        // Convert the HTML string into a document object
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");

        var div = doc.querySelector("product-info");

        if (!holder) {
          return;
        }

        holder.innerHTML = "";
        holder.append(div);

        $this.classList.remove("has-loading");
        document
          .querySelector("#TemplateQuickShopModal")
          .classList.add("active");
        document.querySelector("body").classList.add("lock-overflow");

        const button = document.querySelector(
          "#TemplateQuickShopModal .modal-close"
        );
        if (button) {
          button.onclick = function (event) {
            this.closest("#TemplateQuickShopModal").classList.remove("active");
            document.querySelector("body").classList.remove("lock-overflow");
          };
        }
      });
  }
}
customElements.define("quick-shop", QuickShop);

class AddVariantItem extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  handleClick(event) {
    const id = this.dataset.id;
    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: id,
        quantity: 1,
      }),
    })
      .then(() => {
        fetch(`/cart`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            if (this.closest("cart-items")) {
              this.closest("cart-items").classList.remove("is-empty");
              this.closest("cart-items").innerHTML =
                html.querySelector("cart-items").innerHTML;
            }
            if (
              !document
                .querySelector("body")
                .classList.contains("template-cart")
            ) {
              window.location.href = "/cart";
            }
          });
      })
      .catch((error) => console.error("Error:", error));
  }
}

customElements.define("add-variant-item", AddVariantItem);

class variantSwatchColor extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  handleClick(event) {
    const id = this.dataset.id;
    this.closest(".variant-swatch-btn-main")
      .querySelector("add-variant-item")
      .setAttribute("data-id", id);
    this.closest(".variant-swatch-btn-main")
      .querySelectorAll("variant-swatch-color")
      .forEach((i) => {
        i.classList.remove("active");
      });
    this.classList.add("active");
  }
}

customElements.define("variant-swatch-color", variantSwatchColor);

class ReviewsSwiper extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    if (this.swiper) return;
    this.swiper = new Swiper(this.querySelector(".reviews-swiper"), {
      slidesPerView: 1,
      spaceBetween: 0,
      pagination: {
        el: this.querySelector(".reviews-pagination"),
        clickable: true,
      },
      breakpoints: {
        750: {
          slidesPerView: 1.8,
          spaceBetween: 15,
        },
        1024: {
          slidesPerView: 2.55,
          spaceBetween: 20,
        },
      },
    });
  }
}

customElements.define("cart-reviews-swiper", ReviewsSwiper);

class UpsellProductPdp extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeUpsellProduct(this.dataset.productId);
  }

  initializeUpsellProduct(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const upsellProduct = html.querySelector("upsell-product-pdp");

        if (
          upsellProduct &&
          upsellProduct.querySelectorAll(".cart__upsell").length > 0
        ) {
          this.innerHTML = upsellProduct.innerHTML;
          this.closest(".pdp-upsell-section").classList.remove("tw-hidden");

          new Swiper(
            this.closest(".pdp-upsell-section").querySelector(
              ".swiper-container"
            ),
            {
              slidesPerView: 1,
              spaceBetween: 10,
              loop: false,
              pagination: {
                el: this.closest(".pdp-upsell-section").querySelector(
                  ".swiper-pagination"
                ),
                clickable: true,
              },
              breakpoints: {
                750: {
                  slidesPerView: 1,
                  spaceBetween: 15,
                },
                1024: {
                  slidesPerView: 1.7,
                  spaceBetween: 16,
                },
                1280: {
                  slidesPerView: 2,
                  spaceBetween: 16,
                },
              },
            }
          );
        } else if (
          upsellProduct &&
          upsellProduct.closest(".pdp-upsell-section")
        ) {
          document
            .querySelector(".pdp-upsell-section")
            .classList.add("tw-hidden");
        }
      })
      .catch((e) => {
        document
          .querySelectorAll(".pdp-upsell-section")
          .forEach((upsellSection) => {
            upsellSection.classList.add("tw-hidden");
          });
        console.error(e);
      });
  }
}
customElements.define("upsell-product-pdp", UpsellProductPdp);

document.addEventListener("DOMContentLoaded", function () {
  const buttons = document.querySelectorAll(".quiz-modal-button");
  buttons.forEach((button) => {
    if (button) {
      button.onclick = function () {
        const url = "/pages/privacy-policy?view=quiz";
        fetch(url)
          .then((response) => response.text())
          .then((data) => {
            const parser = new DOMParser();
            const html = parser.parseFromString(data, "text/html");
            const mainContentElement = html.getElementById("MainContent");

            if (mainContentElement) {
              document.querySelector(
                ".quiz-modal-main .quiz-modal__content"
              ).innerHTML = mainContentElement.innerHTML;
              document
                .querySelector(".quiz-modal-main")
                .classList.remove("tw-hidden");
              document
                .querySelector(".quiz-modal-main")
                .classList.add("tw-flex");
              document.querySelector("body").classList.add("overflow-hidden");
              window.quizEvents();
            }
          })
          .catch((error) => {
            console.error("Error fetching data: ", error);
          });
      };
    }
  });

  const closeButton = document.querySelector(".quiz-modal-main .close-modal");
  closeButton.onclick = function () {
    document.querySelector(".quiz-modal-main").classList.add("tw-hidden");
    document.querySelector(".quiz-modal-main").classList.remove("tw-flex");
    document.querySelector("body").classList.remove("overflow-hidden");
  };

  window.quizEvents();
});

window.quizEvents = function () {
  const optionsInputs = document.querySelectorAll(
    '.embr-solution-main .option-item input[type="radio"]'
  );
  optionsInputs.forEach((input) => {
    input.onchange = function () {
      if (this.dataset.src) {
        this.closest(".options-list-container").querySelector(
          ".option-image-container img"
        ).src = this.dataset.src;
      }
      this.closest(".options-list-container")
        .querySelector(".option-image-container")
        .classList.remove("tw-hidden");
      this.closest(".option-list")
        .querySelectorAll(".option-item")
        .forEach((item) => item.classList.remove("active"));
      this.closest(".option-item").classList.add("active");
    };
  });

  const planOptions = document.querySelectorAll('[name="plan-option"]');
  planOptions.forEach((input) => {
    input.onchange = function () {
      const optionContainer = this.closest(".options-list-container");
      if (this.classList.contains("tab-1")) {
        this.closest(".plans-container").querySelector(
          '[name="plan-option-mobile"][data-tab="tab-2"]'
        ).checked = false;
        this.closest(".plans-container").querySelector(
          '[name="plan-option-mobile"][data-tab="tab-1"]'
        ).checked = true;
        optionContainer.classList.add("tab-1-active");
        optionContainer.classList.remove("tab-2-active");
      } else if (this.classList.contains("tab-2")) {
        this.closest(".plans-container").querySelector(
          '[name="plan-option-mobile"][data-tab="tab-1"]'
        ).checked = false;
        this.closest(".plans-container").querySelector(
          '[name="plan-option-mobile"][data-tab="tab-2"]'
        ).checked = true;
        optionContainer.classList.add("tab-2-active");
        optionContainer.classList.remove("tab-1-active");
      }
    };
  });

  const planOptionsMobile = document.querySelectorAll(
    '[name="plan-option-mobile"]'
  );
  planOptionsMobile.forEach((input) => {
    input.onchange = function () {
      if (this.classList.contains("tab-1")) {
        this.closest(".plans-container")
          .querySelector('label[data-tab="tab-1"]')
          .click();
      } else if (this.classList.contains("tab-2")) {
        this.closest(".plans-container")
          .querySelector('label[data-tab="tab-2"]')
          .click();
      }
    };
  });

  const nextButtons = document.querySelectorAll(
    ".navigation-buttons .next-button"
  );
  nextButtons.forEach((button) => {
    button.onclick = function () {
      this.closest(".shopify-section").classList.remove("active");
      this.closest(".shopify-section").nextElementSibling.classList.add(
        "active"
      );

      if (
        this.classList.contains("embr-color-btn") &&
        this.closest(".shopify-section").nextElementSibling.querySelector(
          "product-slider"
        )
      ) {
        setTimeout(() => {
          this.closest(".shopify-section")
            .nextElementSibling.querySelector("product-slider")
            .initProductSlider();
          const variantId = this.closest(".quiz-modal__content").querySelector(
            ".color-card input:checked"
          ).dataset.variantId;
          this.closest(".quiz-modal__content").querySelector(
            'product-info product-form input[name="id"]'
          ).value = variantId;
        }, 300);
      }
      if (
        this.closest(".quiz-modal__content").querySelector(
          ".plan-item input:checked"
        ).value == "embr-subscription"
      ) {
        this.closest(".quiz-modal__content").classList.remove(
          "one-time-purchase"
        );
        this.closest(".quiz-modal__content").classList.add(
          "monthly-subscription"
        );

        const text = this.closest(".quiz-modal__content")
          .querySelector(".plan-item input:checked")
          .closest("label")
          .querySelector(".tab-price").textContent;

        this.closest(".quiz-modal__content")
          .querySelectorAll(".monthly")
          .forEach((i) => {
            i.textContent = text;
          });

        this.closest(".quiz-modal__content")
          .querySelector(".product-features-1")
          .classList.add("tw-hidden");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-1")
          .classList.remove("active");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-2")
          .classList.add("active");

        this.closest(".quiz-modal__content")
          .querySelector(".product-features-3")
          .classList.add("tw-hidden");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-3")
          .classList.remove("active");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-4")
          .classList.add("active");
      } else {
        this.closest(".quiz-modal__content").classList.remove(
          "monthly-subscription"
        );
        this.closest(".quiz-modal__content").classList.add("one-time-purchase");

        this.closest(".quiz-modal__content")
          .querySelector(".product-features-2")
          .classList.add("tw-hidden");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-2")
          .classList.remove("active");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-1")
          .classList.add("active");

        this.closest(".quiz-modal__content")
          .querySelector(".product-features-4")
          .classList.add("tw-hidden");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-4")
          .classList.remove("active");
        this.closest(".quiz-modal__content")
          .querySelector(".product-features-3")
          .classList.add("active");
      }
    };
  });

  const prevButtons = document.querySelectorAll(
    ".navigation-buttons .back-button"
  );
  prevButtons.forEach((button) => {
    button.onclick = function () {
      this.closest(".shopify-section").classList.remove("active");
      this.closest(".shopify-section").previousElementSibling.classList.add(
        "active"
      );
      if (
        this.closest(".shopify-section").previousElementSibling.querySelector(
          "product-slider"
        )
      ) {
        this.closest(".shopify-section")
          .previousElementSibling.querySelector("product-slider")
          .initProductSlider();
      }
    };
  });
};

class quizPurchaseDetail extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initQuizProduct();
  }

  initQuizProduct() {
    let dataHandle = this.dataset.handle;
    let $this = this;
    $this.classList.add("has-loading");

    let holder = this;
    let url = "/products/" + dataHandle + "?view=quiz";
    url = url.replace("//", "/");

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const div = doc.querySelector("product-info");

        if (!holder) {
          return;
        }

        holder.innerHTML = "";
        holder.append(div);
        $this.classList.remove("has-loading");
      });
  }
}

customElements.define("quiz-purchase-detail", quizPurchaseDetail);

class ProductSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initProductSlider();
  }

  initProductSlider() {
    const sliderThumbs = this.querySelector(
      ".slider__thumbs .swiper-container"
    );
    const sliderWrapperThumbs = this.querySelector(
      ".slider__thumbs .swiper-container .swiper-wrapper"
    );
    const sliderImages = this.querySelector(
      ".slider__images .swiper-container"
    );
    const sliderWrapperImages = this.querySelector(
      ".slider__images .swiper-container .swiper-wrapper"
    );
    const sliderDots = this.querySelector(".slider__images .swiper-pagination");
    const mediaJson = this.querySelector(".vertical-media-json");
    const product = this.closest(".product");

    let selectedInput;
    if (product.querySelector("variant-selects input:checked")) {
      selectedInput = product.querySelector("variant-selects input:checked");
    } else if (
      product.closest(".quiz-modal__content") &&
      product
        .closest(".quiz-modal__content")
        .querySelector(".color-container input:checked")
    ) {
      selectedInput = product
        .closest(".quiz-modal__content")
        .querySelector(".color-container input:checked");
    }

    const sliderContainerThumbs = this.querySelector(
      ".slider__thumbs .swiper-container"
    );
    const sliderContainerImages = this.querySelector(
      ".slider__images .swiper-container"
    );

    if (sliderThumbs && sliderImages) {
      if (mediaJson) {
        const jsonData = JSON.parse(mediaJson.textContent);
        if (selectedInput) {
          const selectedImage =
            selectedInput.nextElementSibling?.querySelector("img");
          if (selectedImage) {
            const selectedAlt = selectedImage.getAttribute("alt");

            if (selectedAlt && selectedAlt.includes("|")) {
              const trimmedSelectedAlt = selectedAlt.split("|").pop().trim();
              sliderContainerImages.innerHTML = "";
              sliderContainerThumbs.innerHTML = "";

              sliderWrapperImages.innerHTML = "";
              sliderWrapperThumbs.innerHTML = "";
              for (let i = 0; i < jsonData.length; i++) {
                let product = jsonData[i];
                let src = product.preview_image.src;
                let alt = product.alt;

                if (alt && alt.includes("|")) {
                  const trimmedAlt = alt.split("|").pop().trim();
                  if (trimmedAlt.includes(trimmedSelectedAlt)) {
                    const thumbSlide = document.createElement("div");
                    thumbSlide.classList.add("swiper-slide");

                    const thumbnail = document.createElement("img");
                    thumbnail.src = src;
                    thumbnail.alt = alt;
                    thumbSlide.appendChild(thumbnail);
                    sliderWrapperThumbs.appendChild(thumbSlide);
                    sliderContainerThumbs.appendChild(sliderWrapperThumbs);

                    const imgContainerSlide = document.createElement("div");
                    imgContainerSlide.classList.add("swiper-slide");
                    const containerthumbnail = document.createElement("img");
                    containerthumbnail.src = src;
                    containerthumbnail.alt = alt;
                    imgContainerSlide.appendChild(containerthumbnail);
                    sliderWrapperImages.appendChild(imgContainerSlide);
                    sliderContainerImages.appendChild(sliderWrapperImages);
                  }
                } else if (alt && !alt.includes("|")) {
                  const thumbSlide = document.createElement("div");
                  thumbSlide.classList.add("swiper-slide");

                  const thumbnail = document.createElement("img");
                  thumbnail.src = src;
                  thumbnail.alt = alt;
                  thumbSlide.appendChild(thumbnail);
                  sliderWrapperThumbs.appendChild(thumbSlide);
                  sliderContainerThumbs.appendChild(sliderWrapperThumbs);

                  const imgContainerSlide = document.createElement("div");
                  imgContainerSlide.classList.add("swiper-slide");
                  const containerthumbnail = document.createElement("img");
                  containerthumbnail.src = src;
                  containerthumbnail.alt = alt;
                  imgContainerSlide.appendChild(containerthumbnail);
                  sliderWrapperImages.appendChild(imgContainerSlide);
                  sliderContainerImages.appendChild(sliderWrapperImages);
                }
              }
              sliderWrapperThumbs
                .querySelectorAll(".swiper-slide img")
                .forEach((i, index) => {
                  i.setAttribute("data-index", index);
                });
            }
          }
        }
      }

      const swiperThumbs = new Swiper(sliderThumbs, {
        direction: "horizontal",
        slidesPerView: 5.6,
        spaceBetween: 6,
        freeMode: true,
        breakpoints: {
          990: {
            direction: "vertical",
            slidesPerView: 5,
            spaceBetween: 16,
          },
        },
      });

      const swiperImages = new Swiper(sliderImages, {
        slidesPerView: 1.03,
        spaceBetween: 10,
        pagination: {
          el: sliderDots,
          clickable: true,
        },
        breakpoints: {
          750: {
            slidesPerView: 1,
            spaceBetween: 12,
          },
        },
        thumbs: {
          swiper: swiperThumbs,
        },
        on: {
          slideChange: (e) => {
            console.log("index", swiperImages.realIndex);
            const productSlider = e.el.closest("product-slider");
            const sliderThumbs = productSlider.querySelectorAll(
              ".slider__thumbs .swiper-slide"
            );

            sliderThumbs.forEach((i, index) => {
              i.setAttribute("data-index", index);
            });

            sliderThumbs.forEach((i) => {
              i.classList.remove("swiper-slide-thumb-active");
            });

            const activeThumb = productSlider.querySelector(
              `.slider__thumbs .swiper-slide[data-index="${swiperImages.realIndex}"]`
            );
            if (activeThumb) {
              activeThumb.classList.add("swiper-slide-thumb-active");
            }
          },
        },
      });

      if (product) {
        const selectedInput = product.querySelector(
          "variant-selects input:checked"
        );
        if (selectedInput) {
          const selectedImage =
            selectedInput.nextElementSibling?.querySelector("img");
          if (selectedImage) {
            const alt = selectedImage.getAttribute("alt");
            if (alt && alt.includes("|")) {
              const trimmedAlt = alt.split("|").pop().trim();
              const thumbImg = product.querySelector(
                `.slider__thumbs img[alt*="${trimmedAlt}"]`
              );
              if (thumbImg) {
                const index = thumbImg.dataset.index;
                if (swiperThumbs && swiperImages) {
                  swiperThumbs.slideTo(index);
                  swiperImages.slideTo(index);
                }
              }
            }
          }
        }

        const embrSubscriptionMain = document.querySelector(
          ".embr-subscription-main .color-list-container"
        );
        if (embrSubscriptionMain) {
          const alt = embrSubscriptionMain
            .querySelector("input:checked")
            .closest("label")
            .querySelector("img")
            .getAttribute("alt");

          if (alt && alt.includes("|")) {
            const trimmedAlt = alt.split("|").pop().trim();
            const thumbImg = product.querySelector(
              `.slider__thumbs img[alt*="${trimmedAlt}"]`
            );
            if (thumbImg) {
              const index = thumbImg.dataset.index;
              if (swiperThumbs && swiperImages) {
                swiperThumbs.slideTo(index);
                swiperImages.slideTo(index);
                thumbImg
                  .closest(".swiper-thumbs")
                  .querySelectorAll(".swiper-slide")
                  .forEach((item) => {
                    item.classList.remove("active");
                  });
                thumbImg.closest(".swiper-slide").classList.add("active");
              }
            }
          }
        }
      }
    }
  }
}

customElements.define("product-slider", ProductSlider);

class PlanSelection extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initPlanSelection();
  }

  initPlanSelection() {
    let $this = this;
    const planRadios = $this.querySelectorAll(
      ".selected-plans-radios .plan-radio"
    );
    const subscribeButton = $this.querySelector(
      ".selected-plans-radios .subscribe-now"
    );
    const buyButtons = $this.querySelectorAll(
      ".selected-plans-radios .buy-buttons"
    );
    const selectPlanFirst = $this.querySelector(
      ".selected-plans-radios .select-plan-main-first"
    );
    const selectPlanSecond = $this.querySelector(
      ".selected-plans-radios .select-plan-main-second"
    );
    const ctaSecond = $this.querySelector(
      ".selected-plans-radios .cta-second-button"
    );
    const footer = $this.querySelector(".selected-plans-radios .footer-text");
    const footerSecond = $this.querySelector(
      ".selected-plans-radios .footer-text-second"
    );

    const activePlanTitle = getActivePlanTitle();

    subscribeButton.classList.remove("active");
    buyButtons.forEach((button) => button.classList.add("inactive"));
    if (footer) {
      footer.classList.remove("active");
    }
    if (footerSecond) {
      footerSecond.classList.remove("active");
    }

    selectPlanFirst.classList.add("active");
    subscribeButton.classList.add("active");
    if (footer) {
      footer.classList.add("active");
    }
    const selectedPlanSpan = $this.querySelector(".selected-plan");
    if (selectedPlanSpan) {
      const planTitleSpan = document.createElement("span");
      planTitleSpan.textContent = activePlanTitle;
      selectedPlanSpan.textContent = "Select Plan: ";
      selectedPlanSpan.appendChild(planTitleSpan);
    }

    planRadios.forEach((radio) => {
      radio.addEventListener("change", function () {
        buyButtons.forEach((button) => button.classList.add("inactive"));
        subscribeButton.classList.remove("active");
        selectPlanFirst.classList.remove("active");
        selectPlanSecond.classList.remove("active");
        if (footer) {
          footer.classList.remove("active");
        }
        if (footerSecond) {
          footerSecond.classList.remove("active");
        }

        const planType = radio.getAttribute("data-plan");

        if (planType === "first") {
          selectPlanFirst.classList.add("active");
          subscribeButton.classList.add("active");
          if (footer) {
            footer.classList.add("active");
          }
          if (ctaSecond) {
            ctaSecond.style.display = "none";
          }
          const selectedPlanSpan = $this.querySelector(
            ".selected-plans-radios .selected-plan"
          );
          if (selectedPlanSpan) {
            const planTitleSpan = document.createElement("span");
            planTitleSpan.textContent = activePlanTitle;
            selectedPlanSpan.textContent = "Select Plan: ";
            selectedPlanSpan.appendChild(planTitleSpan);
          }

          buyButtons.forEach((button) => button.classList.add("inactive"));
        } else if (planType === "second") {
          selectPlanSecond.classList.add("active");
          buyButtons.forEach((button) => button.classList.remove("inactive"));
          if (footerSecond) {
            footerSecond.classList.add("active");
          }
          if (ctaSecond) {
            ctaSecond.style.display = "flex";
          }
          if (footerSecond) {
            footerSecond.style.display = "center";
          }
          const activePlanTitle = getActivePlanTitle();
          const selectedPlanSpan = $this.querySelector(
            ".selected-plans-radios .selected-plan"
          );
          if (selectedPlanSpan) {
            const planTitleSpan = document.createElement("span");
            planTitleSpan.textContent = activePlanTitle;
            selectedPlanSpan.textContent = "Select Plan: ";
            selectedPlanSpan.appendChild(planTitleSpan);
          }

          subscribeButton.classList.remove("active");
        }
      });
    });

    function getActivePlanTitle() {
      let activeDiv =
        $this.querySelector(
          ".selected-plans-radios .select-plan-main-first.active"
        ) ||
        $this.querySelector(
          ".selected-plans-radios .select-plan-main-second.active"
        );

      if (activeDiv) {
        const titleElement = activeDiv.querySelector("label span");

        return titleElement ? titleElement.textContent : null;
      }
      return null;
    }

    $this.querySelectorAll(".select-plan-block").forEach((block) => {
      block.onclick = function () {
        this.querySelector("label").click();
      };
    });
  }
}

customElements.define("selected-plans-radios", PlanSelection);

document.addEventListener("DOMContentLoaded", function () {
  const buttons = document.querySelectorAll(".review-part");
  buttons.forEach((button) => {
    button.onclick = function () {
      document
        .querySelector('[data-bv-show="reviews"]')
        .scrollIntoView({ behavior: "smooth" });
    };
  });
});

var GroupEffect = class {
  constructor(childrenEffects) {
    this._childrenEffects = childrenEffects;
    this._finished = Promise.resolve();
  }
  get finished() {
    return this._finished;
  }
  get animationEffects() {
    return this._childrenEffects.flatMap((effect) => {
      return effect instanceof CustomKeyframeEffect
        ? effect
        : effect.animationEffects;
    });
  }
};

var ParallelEffect = class extends GroupEffect {
  play() {
    const promises = [];
    for (const effect of this._childrenEffects) {
      effect.play();
      promises.push(effect.finished);
    }
    this._finished = Promise.all(promises);
  }
};

// js/helper/animation.js
var CustomAnimation = class {
  constructor(effect) {
    this._effect = effect;
    this._playState = "idle";
    this._finished = Promise.resolve();
  }
  get finished() {
    return this._finished;
  }
  get animationEffects() {
    return this._effect instanceof CustomKeyframeEffect
      ? [this._effect]
      : this._effect.animationEffects;
  }
  cancel() {
    this.animationEffects.forEach((animationEffect) =>
      animationEffect.cancel()
    );
  }
  finish() {
    this.animationEffects.forEach((animationEffect) =>
      animationEffect.finish()
    );
  }
  play() {
    this._playState = "running";
    this._effect.play();
    this._finished = this._effect.finished;
    this._finished.then(
      () => {
        this._playState = "finished";
      },
      (rejection) => {
        this._playState = "idle";
      }
    );
  }
};
var CustomKeyframeEffect = class {
  constructor(target, keyframes, options = {}) {
    if (!target) {
      return;
    }
    if ("Animation" in window) {
      this._animation = new Animation(
        new KeyframeEffect(target, keyframes, options)
      );
    } else {
      options["fill"] = "forwards";
      this._animation = target.animate(keyframes, options);
      this._animation.pause();
    }
    this._animation.addEventListener("finish", () => {
      target.style.opacity = keyframes.hasOwnProperty("opacity")
        ? keyframes["opacity"][keyframes["opacity"].length - 1]
        : null;
      target.style.visibility = keyframes.hasOwnProperty("visibility")
        ? keyframes["visibility"][keyframes["visibility"].length - 1]
        : null;
    });
  }
  get finished() {
    if (!this._animation) {
      return Promise.resolve();
    }
    return this._animation.finished
      ? this._animation.finished
      : new Promise((resolve) => (this._animation.onfinish = resolve));
  }
  play() {
    if (this._animation) {
      this._animation.startTime = null;
      this._animation.play();
    }
  }
  cancel() {
    if (this._animation) {
      this._animation.cancel();
    }
  }
  finish() {
    if (this._animation) {
      this._animation.finish();
    }
  }
};

// js/custom-element/behavior/prev-next-buttons.js
var PrevNextButtons = class extends HTMLElement {
  connectedCallback() {
    this.prevButton = this.querySelector("button:first-of-type");
    this.nextButton = this.querySelector("button:last-of-type");
    this.prevButton.addEventListener("click", () =>
      this.prevButton.dispatchEvent(
        new CustomEvent("prev-next:prev", { bubbles: true })
      )
    );
    this.nextButton.addEventListener("click", () =>
      this.nextButton.dispatchEvent(
        new CustomEvent("prev-next:next", { bubbles: true })
      )
    );
  }
  set isPrevDisabled(value) {
    this.prevButton.disabled = value;
  }
  set isNextDisabled(value) {
    this.nextButton.disabled = value;
  }
};
var PrevButton = class extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener("click", () =>
      this.dispatchEvent(new CustomEvent("prev-next:prev", { bubbles: true }))
    );
  }
};
var NextButton = class extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener("click", () =>
      this.dispatchEvent(new CustomEvent("prev-next:next", { bubbles: true }))
    );
  }
};
window.customElements.define("prev-next-buttons", PrevNextButtons);
window.customElements.define("prev-button", PrevButton, { extends: "button" });
window.customElements.define("next-button", NextButton, { extends: "button" });

// js/helper/media-features.js
var MediaFeatures = class {
  static prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  static supportsHover() {
    return window.matchMedia("(pointer: fine)").matches;
  }
};


// js/custom-element/custom-html-element.js
var CustomHTMLElement = class extends HTMLElement {
  constructor() {
    super();
    this._hasSectionReloaded = false;
    if (Shopify.designMode) {
      this.rootDelegate.on("shopify:section:select", (event) => {
        const parentSection = this.closest(".shopify-section");
        if (event.target === parentSection && event.detail.load) {
          this._hasSectionReloaded = true;
        }
      });
    }
  }
  get rootDelegate() {
    return this._rootDelegate = this._rootDelegate || new main_default(document.documentElement);
  }
  get delegate() {
    return this._delegate = this._delegate || new main_default(this);
  }
  showLoadingBar() {
    triggerEvent(document.documentElement, "theme:loading:start");
  }
  hideLoadingBar() {
    triggerEvent(document.documentElement, "theme:loading:end");
  }
  untilVisible(intersectionObserverOptions = { rootMargin: "30px 0px", threshold: 0 }) {
    const onBecameVisible = () => {
      this.classList.add("became-visible");
      this.style.opacity = "1";
    };
    return new Promise((resolve) => {
      if (window.IntersectionObserver) {
        this.intersectionObserver = new IntersectionObserver((event) => {
          if (event[0].isIntersecting) {
            this.intersectionObserver.disconnect();
            requestAnimationFrame(() => {
              resolve();
              onBecameVisible();
            });
          }
        }, intersectionObserverOptions);
        this.intersectionObserver.observe(this);
      } else {
        resolve();
        onBecameVisible();
      }
    });
  }
  disconnectedCallback() {
    var _a;
    this.delegate.destroy();
    this.rootDelegate.destroy();
    (_a = this.intersectionObserver) == null ? void 0 : _a.disconnect();
    delete this._delegate;
    delete this._rootDelegate;
  }
};

// js/custom-element/section/multi-column/multi-column.js
var MultiColumn = class extends CustomHTMLElement {
  connectedCallback() {
    if (!this.hasAttribute("stack")) {
      this.multiColumnInner = this.querySelector(".multi-column__inner");
      this.addEventListener("prev-next:prev", this.previous.bind(this));
      this.addEventListener("prev-next:next", this.next.bind(this));
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          event.target.scrollIntoView({
            inline: "center",
            block: "nearest",
            behavior: event.detail.load ? "auto" : "smooth",
          });
        });
      }
    }
    if (this.hasAttribute("stagger-apparition")) {
      this._setupVisibility();
    }
  }
  async _setupVisibility() {
    await this.untilVisible({ threshold: Math.min(50 / this.clientHeight, 1) });
    const prefersReducedMotion = MediaFeatures.prefersReducedMotion();
    const animation = new CustomAnimation(
      new ParallelEffect(
        Array.from(this.querySelectorAll(".multi-column__item")).map(
          (item, index) => {
            return new CustomKeyframeEffect(
              item,
              {
                opacity: [0, 1],
                transform: [
                  `translateY(${
                    MediaFeatures.prefersReducedMotion()
                      ? 0
                      : window.innerWidth < 1e3
                      ? 35
                      : 60
                  }px)`,
                  "translateY(0)",
                ],
              },
              {
                duration: 600,
                delay: prefersReducedMotion ? 0 : 100 * index,
                easing: "ease",
              }
            );
          }
        )
      )
    );
    this._hasSectionReloaded ? animation.finish() : animation.play();
  }
  previous(event) {
    console.log("previous call");
    // const directionFlip = window.themeVariables.settings.direction === "ltr" ? 1 : -1, columnGap = parseInt(getComputedStyle(this).getPropertyValue("--multi-column-column-gap"));
    const directionFlip =
        document.documentElement.getAttribute("dir") === "ltr" ? 1 : 1,
      columnGap = parseInt(
        getComputedStyle(this).getPropertyValue("--multi-column-column-gap")
      );
    event.target.nextElementSibling.removeAttribute("disabled");
    event.target.toggleAttribute(
      "disabled",
      this.multiColumnInner.scrollLeft * directionFlip -
        (this.multiColumnInner.clientWidth + columnGap) <=
        0
    );
    this.multiColumnInner.scrollBy({
      left: -(this.multiColumnInner.clientWidth + columnGap) * directionFlip,
      behavior: "smooth",
    });
  }
  next(event) {
    console.log("next call");
    const directionFlip =
        document.documentElement.getAttribute("dir") === "ltr" ? 1 : 1,
      columnGap = parseInt(
        getComputedStyle(this).getPropertyValue("--multi-column-column-gap")
      );
    event.target.previousElementSibling.removeAttribute("disabled");
    event.target.toggleAttribute(
      "disabled",
      this.multiColumnInner.scrollLeft * directionFlip +
        (this.multiColumnInner.clientWidth + columnGap) * 2 >=
        this.multiColumnInner.scrollWidth
    );
    this.multiColumnInner.scrollBy({
      left: (this.multiColumnInner.clientWidth + columnGap) * directionFlip,
      behavior: "smooth",
    });
  }
};
window.customElements.define("multi-column", MultiColumn);

// Simple CTA button variant updater
function updateCtaButtonOnVariantChange() {
  // Find the specific CTA button
  const ctaButton = document.querySelector(".subscribe-now");
  if (!ctaButton) return;

  // Store the base URL (without variant parameters)
  const baseUrl = ctaButton.getAttribute("href");
  if (!baseUrl) return;

  // Listen for variant changes
  document.addEventListener("change", function (event) {
    // Check if this is a variant selection change
    if (event.target.className == "product-variant-id") {
      try {
        // Get the current selected variant
        const selectedVariantId = event.target.value;
        if (!selectedVariantId) return;
        console.log("Selected variant ID", selectedVariantId);

        // Create new URL with variant parameters
        const url = new URL(baseUrl, window.location.origin);

        console.log(url.search);

        const params = new URLSearchParams(url.search);

        // Add variant based on variant ID
        if (selectedVariantId == "39271596032118") {
          url.pathname += 'checkout';
          params.set(
            "products",
            "wave-2\(color\=rose-gold\,condition\=certified-refurbished\)"
          );
        }
        if (selectedVariantId == "39271595999350") {
          url.pathname += 'checkout';
          params.set(
            "products",
            "wave-2\(color\=black\,condition\=certified-refurbished\)"
          );
        }

        console.log("Search Params", params.toString());

        url.search = params.toString();

        console.log("Final URL", url.toString());

        // Update the button href
        ctaButton.setAttribute("href", url.toString());
      } catch (e) {
        console.error("Error updating CTA button href:", e);
      }
    }
  });
}

document.addEventListener("DOMContentLoaded", updateCtaButtonOnVariantChange);

function updateAccordionEmbrOnClick() {
  const accordionRows = document.querySelectorAll('.accordion-row');

  accordionRows.forEach(row => {
    const toggleButton = row.querySelector('.accordion-toggle');
    const accordionContent = row.querySelector('.accordion-content');

    toggleButton.addEventListener('click', () => {
    const isOpen = accordionContent.classList.toggle('open');
    toggleButton.classList.toggle('rotate');

    toggleButton.innerHTML = isOpen
      ? 'Read less <i class="fas fa-chevron-up arrow-icon"></i>'
      : 'Read more <i class="fas fa-chevron-down arrow-icon"></i>';
  });
  })
}

document.addEventListener("DOMContentLoaded", updateAccordionEmbrOnClick);
